import React, { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Collapse,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useNpsList } from './hooks/useNpsList';
import { useDeleteNps } from './hooks/useDeleteNps';
import Loading from '../../components/loading';
import SuccessMessage from '../../components/successMessage';
import ErrorMessage from '../../components/errorMessage';
import NpsModal from './npsModal';

const STATUS_COLOR = {
  CREATED: 'info',
  PENDING: 'warning',
  ANSWERED: 'success',
  EXPIRED: 'default',
};

const CLASSIFICATION_COLOR = {
  PROMOTER: 'success',
  NEUTRAL: 'warning',
  DETRACTOR: 'error',
};

const CLASSIFICATION_LABEL = {
  PROMOTER: 'Promotor',
  NEUTRAL: 'Neutro',
  DETRACTOR: 'Detrator',
};

const STATUS_LABEL = {
  CREATED: 'Criado',
  PENDING: 'Pendente',
  ANSWERED: 'Respondido',
  EXPIRED: 'Expirado',
};

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function NpsRow({ nps, onDeleteClick }) {
  const [open, setOpen] = useState(false);
  const hasAnswers = nps.questionsWithAnswers?.length > 0;

  return (
    <>
      <TableRow hover>
        <TableCell>
          {hasAnswers && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{nps.userEmail}</TableCell>
        <TableCell align="center">
          {nps.score !== null && nps.score !== undefined ? nps.score : '—'}
        </TableCell>
        <TableCell>
          <Chip
            label={STATUS_LABEL[nps.status] ?? nps.status}
            color={STATUS_COLOR[nps.status] ?? 'default'}
            size="small"
          />
        </TableCell>
        <TableCell>
          {nps.classification ? (
            <Chip
              label={CLASSIFICATION_LABEL[nps.classification] ?? nps.classification}
              color={CLASSIFICATION_COLOR[nps.classification] ?? 'default'}
              size="small"
            />
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell>{formatDate(nps.createdAt)}</TableCell>
        <TableCell>{formatDate(nps.answeredAt)}</TableCell>
        <TableCell align="right">
          {nps.status !== 'ANSWERED' && (
            <IconButton size="small" color="error" onClick={() => onDeleteClick(nps)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </TableCell>
      </TableRow>

      {hasAnswers && (
        <TableRow>
          <TableCell colSpan={8} sx={{ py: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ mx: 2, my: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Perguntas e Respostas
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Pergunta</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Resposta</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nps.questionsWithAnswers.map((qa, index) => (
                      <TableRow key={index}>
                        <TableCell>{qa.question}</TableCell>
                        <TableCell>{qa.answer ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

NpsRow.propTypes = {
  nps: PropTypes.shape({
    userEmail: PropTypes.string,
    score: PropTypes.number,
    status: PropTypes.string,
    classification: PropTypes.string,
    createdAt: PropTypes.string,
    answeredAt: PropTypes.string,
    questionsWithAnswers: PropTypes.arrayOf(
      PropTypes.shape({
        question: PropTypes.string,
        answer: PropTypes.string,
      }),
    ),
  }).isRequired,
  onDeleteClick: PropTypes.func.isRequired,
};

export default function NpsList() {
  const navigate = useNavigate();

  const { npsList, isLoading, error: listError, refetch: getNpsList } = useNpsList();
  const { remove } = useDeleteNps();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [npsToDelete, setNpsToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage('Não foi possível carregar as pesquisas de NPS.');
    }
  }, [listError]);

  const onNpsSaveSuccess = (returnFromApi) => {
    setShowSuccessMessage(true);
    setSuccessMessage(
      returnFromApi.data.message || returnFromApi.data || 'Pesquisa criada com sucesso!',
    );
    setIsModalOpen(false);
    getNpsList();
  };

  const confirmDelete = () => {
    setIsDeleting(true);
    remove(npsToDelete.id)
      .then(() => {
        setShowSuccessMessage(true);
        setSuccessMessage('NPS excluído com sucesso!');
        setNpsToDelete(null);
        getNpsList();
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          navigate('/expired-session');
        } else {
          setShowErrorMessage(true);
          setErrorMessage('Não foi possível excluir o NPS.');
        }
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  return (
    <>
      <Loading isToHidden={!isLoading} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title={
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              NPS
            </Typography>
          }
          action={
            <Button variant="outlined" onClick={() => setIsModalOpen(true)}>
              Criar Nova Pesquisa
            </Button>
          }
        />
        <CardContent>
          {!isLoading && npsList.length === 0 ? (
            <Typography align="center" sx={{ py: 4 }}>
              Nenhuma pesquisa de NPS cadastrada.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="5%" />
                    <TableCell sx={{ fontWeight: 'bold' }}>E-mail</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">
                      Nota
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Classificação</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Criado em</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Respondido em</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {npsList.map((nps) => (
                    <NpsRow
                      key={nps.userEmail + nps.createdAt}
                      nps={nps}
                      onDeleteClick={setNpsToDelete}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <NpsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSaveSuccess={onNpsSaveSuccess}
      />

      <Dialog open={!!npsToDelete} onClose={() => setNpsToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir NPS</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o NPS de <strong>{npsToDelete?.userEmail}</strong>? Essa
            ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNpsToDelete(null)} disabled={isDeleting}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={isDeleting}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
